// ── AllForwardRatesPage ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmtDate, exportCSV } from "../../../shared/format";
import { Btn, Card, FInput, FSelect, Icon, STh, SectionHeader, Td, TdNum, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const AllForwardRatesPage = () => {
    const { api, role, view, setModal, setConfirm, delRec } = useApp();
  
    const [rows,        setRows]        = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [sortKey,     setSortKey]     = useState('curvedate');
    const [sortDir,     setSortDir]     = useState('desc');
    const [search,      setSearch]      = useState('');
    const [filterIndex, setFilterIndex] = useState('');
    // One curve = one upload date. '' = every curve.
    const [filterCurve, setFilterCurve] = useState('');
    const load = async () => {
      setLoading(true);
      try { setRows(await api.get('getForwardRates')); } catch(e) {}
      setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const toggle = key => {
      if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
      else { setSortKey(key); setSortDir('asc'); }
    };

    const indexes = [...new Set(rows.map(r => r.indexname).filter(Boolean))].sort();

    const displayed = rows
      .filter(r => {
        const q = search.toLowerCase();
        return (!q || (r.indexname||'').toLowerCase().includes(q)
                    || (r.ratedescription||'').toLowerCase().includes(q)
                    || (r.curvedate||'').includes(q)
                    || (r.ratedate||'').includes(q))
            && (!filterIndex || r.indexname === filterIndex)
            && (!filterCurve || String(r.curvedate || '').slice(0, 10) === filterCurve);
      })
      .sort((a, b) => {
        let av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
        if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });

    const indexNames = [...new Set(rows.map(r => r.indexname).filter(Boolean))].sort();
    const FILTER_OPTIONS = [
      { value:'', label:'All Indexes' },
      ...indexNames.map(i => ({ value:i, label:i }))
    ];

    // Curve dates for the index in view, newest first — picking SOFR should
    // not offer a date that only Prime was uploaded on.
    const curveDates = [...new Set(rows
      .filter(r => !filterIndex || r.indexname === filterIndex)
      .map(r => String(r.curvedate || '').slice(0, 10)).filter(Boolean))].sort().reverse();
    const CURVE_OPTIONS = [
      { value:'', label:'All Curve Dates' },
      ...curveDates.map(d => ({ value:d, label:fmtDate(d) }))
    ];
    // A curve date that the index change has made impossible is cleared rather
    // than left filtering to nothing.
    useEffect(() => {
      if (filterCurve && !curveDates.includes(filterCurve)) setFilterCurve('');
    }, [filterIndex, rows]); // eslint-disable-line react-hooks/exhaustive-deps

    // What is on screen, in the order it is on screen. Dates as YYYY-MM-DD and
    // the rate as stored (a decimal: 0.0385 = 3.85%), so it pastes straight
    // into the Mark Proof workbook's Curve sheet.
    const exportRows = () => exportCSV(
      displayed.map(r => ({
        curvedate: String(r.curvedate || '').slice(0, 10),
        indexname: r.indexname || '',
        ratedate:  String(r.ratedate || '').slice(0, 10),
        rate:      r.rate,
        ratedescription: r.ratedescription || '',
      })),
      ['curvedate', 'indexname', 'ratedate', 'rate', 'ratedescription'],
      'forward_rates' + (filterIndex ? '_' + filterIndex : '') + (filterCurve ? '_' + filterCurve : '') + '.csv');

    return <>
      <SectionHeader title="Forward Rates" />

      <Card style={{ marginBottom:16, padding:16 }}>
        <div style={{ display:'flex', gap:12, alignItems:'center', flexWrap:'wrap' }}>
          <FInput
            placeholder="Search..."
            value={search}
            onChange={setSearch}
            style={{ width:240 }}
          />
          <FSelect
            value={filterIndex}
            onChange={v => setFilterIndex(typeof v === 'object' ? (v?.value ?? '') : (v ?? ''))}
            options={FILTER_OPTIONS}
            style={{ width:160 }}
          />
          <FSelect
            value={filterCurve}
            onChange={v => setFilterCurve(typeof v === 'object' ? (v?.value ?? '') : (v ?? ''))}
            options={CURVE_OPTIONS}
            style={{ width:170 }}
          />
          <span style={{ fontSize:12, color:'var(--color-text-secondary)' }}>
            {displayed.length.toLocaleString('en-US')} rate{displayed.length === 1 ? '' : 's'}
          </span>
          <div style={{ flex:1 }} />
          <Btn onClick={exportRows} disabled={!displayed.length}>Export CSV</Btn>
          <Btn
            variant="primary"
            onClick={() => setModal({ type:'forward-rate-add', onDone:load })}
          >
            + Add Rate
          </Btn>
        </div>
      </Card>

      <Card style={{ overflowX:'auto' }}>
        {loading
          ? <div style={{ padding:32, textAlign:'center', color:'var(--color-text-secondary)' }}>Loading...</div>
          : <table style={{ width:'100%', borderCollapse:'collapse', whiteSpace:'nowrap' }}>
              <thead><tr>
                <STh label="Curve Date"   sortKey="curvedate"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Index"        sortKey="indexname"       currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Rate Date"    sortKey="ratedate"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <STh label="Rate"         sortKey="rate"            currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
                <STh label="Description"  sortKey="ratedescription" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
                <Th></Th>
              </tr></thead>
              <tbody>
                {displayed.map(r => (
                  <tr key={r.id}
                    style={{ cursor:'pointer' }}
                    onClick={() => setModal({ type:'forward-rate-view', rec:r })}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--color-background-secondary)'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <Td><span style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>{fmtDate(r.curvedate)}</span></Td>
                    <Td>
                      <span style={{ background:B.cyanLight, color:B.cyanDark, padding:'2px 8px', borderRadius:4, fontSize:12, fontWeight:600 }}>
                        {r.indexname||'—'}
                      </span>
                    </Td>
                    <Td><span style={{ fontFamily:'var(--font-mono)', fontSize:12 }}>{fmtDate(r.ratedate)}</span></Td>
                    <TdNum style={{ fontWeight:500 }}>
                      {r.rate != null ? (r.rate * 100).toFixed(4) + '%' : '—'}
                    </TdNum>
                    <Td style={{ color:'var(--color-text-secondary)', fontSize:12, maxWidth:300, overflow:'hidden', textOverflow:'ellipsis' }}>
                      {r.ratedescription||'—'}
                    </Td>

                    <Td>
                      <Btn onClick={(e)=>{ e.stopPropagation(); setModal({type:'forward-rate-view',rec:r}); }}><Icon type="view" /></Btn>
                      {role.canEdit&&<Btn onClick={(e)=>{ e.stopPropagation(); setModal({type:'forward-rate-edit',rec:r,onDone:load}); }}><Icon type="edit" /></Btn>}
                      {role.canDelete&&<Btn variant="danger" onClick={(e)=>{ e.stopPropagation(); setConfirm({msg:'Delete this forward rate?',onConfirm:async()=>{
                        await delRec('ForwardRates',r.id,'Deleted forward rate',load);
                        setConfirm(null);
                      }}); }}><Icon type="delete" /></Btn>}
                    </Td>





                  </tr>
                ))}
                {!displayed.length && (
                  <tr><td colSpan={6} style={{ textAlign:'center', padding:32, color:'var(--color-text-secondary)', fontSize:13 }}>
                    No forward rates found.
                  </td></tr>
                )}
              </tbody>
            </table>
        }
      </Card>
    </>;
  };
